import { useChangePasswordMutation } from "Api/Mutations/User/useChangePasswordMutation";
import { ResponsiveDialog } from "Components/Shared/Dialogs/ResponsiveDialog";
import { FormPasswordInput } from "Components/Shared/Inputs/Form/FormPasswordInput";
import {
  type ChangePasswordFormModel,
  doNewPasswordsMatch,
  getChangePasswordValidationSchema,
  isNewPasswordDifferentFromOld,
} from "Components/UserAccount/Shared/UserSettings/ChangePasswordValidation";
import { PasswordRequirements } from "Components/Shared/Inputs/PasswordRequirements";
import { RequirementChecklist } from "Components/Shared/Inputs/RequirementChecklist";
import { resetUser } from "State/Auth/AuthReducer";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Stack } from "@mui/material";
import { useNavigate } from "Navigation/useNavigate";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { toast } from "sonner";

const RESOURCES = Resources.Components.UserAccount.ChangePassword;

type ChangePasswordApiError = {
  status?: number;
  data?: { errors?: Record<string, unknown> };
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const ChangePasswordDialog = ({ isOpen, onClose }: Props) => {
  const { t } = useResource();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    setError,
    reset,
    watch,
    formState: { isValid },
  } = useForm<ChangePasswordFormModel>({
    resolver: zodResolver(getChangePasswordValidationSchema(t)),
    mode: "onChange",
    defaultValues: { oldPassword: "", newPassword: "", newPasswordConfirm: "" },
  });

  const { mutate, isPending } = useChangePasswordMutation({
    onSuccess: () => {
      toast.success(t(RESOURCES.Success));
      dispatch(
        resetUser({
          onAfterReset: () => navigate(appUrl("sign-in"), { replace: true }),
        }),
      );
    },
    onError: (e) => {
      const error = e as ChangePasswordApiError;

      if (error.status === 429) {
        return toast.error(t(RESOURCES.RateLimitError));
      }

      const fieldErrors = error.data?.errors;

      if (fieldErrors?.OldPassword) {
        return setError("oldPassword", {
          message: t(RESOURCES.WrongOldPassword),
        });
      }

      toast.error(t(RESOURCES.GenericError));
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const submit = (values: ChangePasswordFormModel) => {
    mutate(values);
  };

  const newPassword = watch("newPassword");
  const oldPassword = watch("oldPassword");
  const newPasswordConfirm = watch("newPasswordConfirm");

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={t(RESOURCES.DialogTitle)}
    >
      <Stack component="form" gap={2} onSubmit={handleSubmit(submit)}>
        <FormPasswordInput
          control={control}
          name="oldPassword"
          label={t(RESOURCES.OldPasswordLabel)}
        />
        <FormPasswordInput
          control={control}
          name="newPassword"
          label={t(RESOURCES.NewPasswordLabel)}
          isValidationHidden
        />
        <PasswordRequirements
          password={newPassword ?? ""}
          extraRequirements={[
            {
              id: "differsFromCurrent",
              label: t(RESOURCES.NewMustDiffer),
              isSatisfied:
                (newPassword ?? "").length > 0 &&
                isNewPasswordDifferentFromOld(
                  newPassword ?? "",
                  oldPassword ?? "",
                ),
            },
          ]}
        />
        <Stack gap={2} mt={1}>
          <FormPasswordInput
            control={control}
            name="newPasswordConfirm"
            label={t(RESOURCES.NewPasswordConfirmLabel)}
            isValidationHidden
          />
          <RequirementChecklist
            items={[
              {
                id: "passwordsMatch",
                label: t(Resources.Validation.Password.MatchRequirement),
                isSatisfied:
                  (newPasswordConfirm ?? "").length > 0 &&
                  doNewPasswordsMatch(
                    newPassword ?? "",
                    newPasswordConfirm ?? "",
                  ),
              },
            ]}
          />
        </Stack>
        <Stack direction="row" gap={2} justifyContent="flex-end" mt={1}>
          <Button color="primary" onClick={handleClose} disabled={isPending}>
            {t(RESOURCES.Cancel)}
          </Button>
          <Button
            color="info"
            type="submit"
            variant="contained"
            disabled={!isValid}
            loading={isPending}
            loadingPosition="start"
          >
            {t(RESOURCES.Submit)}
          </Button>
        </Stack>
      </Stack>
    </ResponsiveDialog>
  );
};
